import { t } from "@travily/i18n";
import { CameraIcon, ExitIcon } from "@ui/components/Icon";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { DropZone, type DropZoneProps, FileTrigger, useSingleFileDrop } from "@ui/primitives/DropZone";
import { composeRenderProps } from "@ui/primitives/shared";

const standardDropZone = tv({
  slots: {
    root: "relative overflow-clip rounded-xl",
    trigger: [
      "absolute inset-0 flex cursor-pointer items-center justify-center rounded-xl outline-none",
      "focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2",
    ],
    placeholder: [
      "flex size-full items-center justify-center rounded-xl",
      "border border-neutral-100 bg-neutral-50 text-neutral-400",
    ],
    preview: "absolute inset-0 size-full object-cover",
    overlay: "pointer-events-none absolute inset-0 bg-black/20",
    removeButton: [
      "absolute end-2 top-2 z-10 flex size-7 cursor-pointer items-center justify-center",
      "rounded-full bg-white/80 text-neutral-600 backdrop-blur-sm",
      "hover:bg-white focus-visible:bg-white pressed:bg-white",
    ],
  },
  variants: {
    isDropTarget: {
      true: {
        root: "ring-2 ring-blue-400 ring-offset-2",
      },
    },
  },
});

export interface StandardDropZoneProps {
  /** Accessible name for the drop zone and file trigger. */
  "aria-label": string;
  /** MIME types to accept, e.g. `["image/png", "image/jpeg"]`. */
  acceptedFileTypes?: string[];
  /** Controls outer dimensions — the component is layout-agnostic. */
  className?: string;
  /** Called with the selected or dropped `File`. */
  onFileSelect: (file: File) => void;
  /** When provided alongside `previewSrc`, renders a remove button. */
  onRemove?: () => void;
  /** Image URL to show as a filled preview. Falsy renders the empty placeholder. */
  previewSrc?: string;
}

/**
 * Styled image upload area combining DropZone and FileTrigger.
 * Empty state shows a camera icon placeholder; filled state shows an image preview
 * with an optional remove button. Accepts files via click or drag-and-drop.
 */
export function StandardDropZone({
  "aria-label": ariaLabel,
  acceptedFileTypes,
  className,
  onFileSelect,
  onRemove,
  previewSrc,
}: StandardDropZoneProps) {
  const styles = standardDropZone();
  const { onDrop, onSelect } = useSingleFileDrop(onFileSelect);

  return (
    <DropZone
      aria-label={ariaLabel}
      className={
        composeRenderProps(className, (className, renderProps) =>
          styles.root({ ...renderProps, className }),
        ) as DropZoneProps["className"]
      }
      onDrop={onDrop}
    >
      {previewSrc && (
        <>
          <img alt="" className={styles.preview()} src={previewSrc} />
          <div aria-hidden="true" className={styles.overlay()} />
        </>
      )}
      {/* Mounted in both states, so a filled zone can be replaced rather than only
          removed. Named for the action, not the field — the drop zone around it
          already carries `aria-label`, and two controls sharing one name reads as
          a duplicate. */}
      <FileTrigger acceptedFileTypes={acceptedFileTypes} onSelect={onSelect}>
        <Button
          aria-label={previewSrc ? t("components.standard_drop_zone.change") : t("components.standard_drop_zone.add")}
          className={styles.trigger()}
        >
          {!previewSrc && (
            <span className={styles.placeholder()}>
              <CameraIcon aria-hidden="true" className="size-5" />
            </span>
          )}
        </Button>
      </FileTrigger>
      {previewSrc && onRemove && (
        <Button
          aria-label={t("components.standard_drop_zone.remove")}
          className={styles.removeButton()}
          onPress={onRemove}
        >
          <ExitIcon aria-hidden="true" className="size-4" />
        </Button>
      )}
    </DropZone>
  );
}
